import React, { memo, useContext, useEffect, useState, useRef } from 'react';
import { Table, ActionIcon, Select, Pagination, Modal, Box, Title, Group, Button, Text, Paper, Space, Flex, TextInput, Divider, LoadingOverlay } from '@mantine/core';
import { IoAddOutline, IoTrashOutline } from "react-icons/io5";
import { FaRegEdit } from "react-icons/fa";
import classes from "./ProducTable.module.css"
import { useNavigate } from 'react-router-dom';
import { useDisclosure } from '@mantine/hooks';
import { MdOutlineRemoveRedEye } from "react-icons/md";
import DeleterowData from './DeleterowData/DeleterowData.component';
import ViewProductRow from './ViewProductrow/ViewProductrow.component';
import { baseUrl } from '../../../../../Constants/constant';
import { authContext } from '../../../../../context/AuthProvider.component';
import { notifications } from '@mantine/notifications';
import ProductDetailsComponent from '../ProductSelection/ProductDetails.component';

interface ProductTableProps {
  onComplete: () => void;
  editLink?: string;
  search?: string;
  isopen?: () => void;
}

type ErrorsType = {
  file_name?: string; // Optional property to avoid TypeScript error
};

const ProductTable: React.FC<ProductTableProps> = ({
  onComplete,
  editLink,
  search,
  isopen
}) => {
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [refresh, setRefresh] = useState<number>(0);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [selectedComputeId, setSelectedComputeId] = useState<any>(null);
  const [errors, setErrors] = useState<any>();
  const [fileName, setFileName] = useState<string>('')
  const [confirmDeleteOpened, { open: openConfirmDelete, close: closeConfirmDelete }] = useDisclosure(false);
  const [successModalOpened, { open: openSuccessModal, close: closeSuccessModal }] = useDisclosure(false);
  const [viewModalOpened, { open: openViewModal, close: closeViewModal }] = useDisclosure(false);
  const [addComponentModalOpened, { open: openAddComponentModal, close: closeAddComponentModal }] = useDisclosure(false);
  const [opened, { open, close }] = useDisclosure(false);
  const [isLoading, setIsLoading] = useState(false);
  const [requestData, setRequestData] = useState<any>(null);
  const isProcessingRef = useRef(false);
  const isTableLoadingRef = useRef(false);
  const hasCompletedRef = useRef(false);

  const { computeId, draftLca, setDraftLca } = useContext<any>(authContext);

  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  // Add comprehensive re-render tracking
  const renderCount = useRef(0);
  renderCount.current += 1;

  // State for API data
  const [datalist, setDatalist] = useState<any>(null);
  const [listLoading, setListLoading] = useState(false);

  // Fetch table data function
  const gettable = async (url: string) => {
    // Prevent multiple simultaneous calls using ref
    if (isTableLoadingRef.current) {
      return;
    }
    
    try {
      isTableLoadingRef.current = true;
      setListLoading(true);
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          token: token || "",
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      
      const result = await response.json();
      setDatalist(result);
    } catch (error) {
      console.error('Error fetching table data:', error);
      notifications.show({
        title: 'Error',
        message: 'Error loading table data',
        color: 'red',
      });
    } finally {
      setListLoading(false);
      isTableLoadingRef.current = false;
    }
  };

  useEffect(() => {
    // Don't fetch table data if we've already completed navigation
    if (hasCompletedRef.current) {
      return;
    }
    
    if (computeId) {
      gettable(`${baseUrl}compute/lca/${computeId}?is_draft=${draftLca}`);
    }
  }, [page, limit, refresh, computeId, draftLca]);

  // Handle API call when requestData changes
  useEffect(() => {
    // Early return if no request data or already completed
    if (!requestData || hasCompletedRef.current) {
      return;
    }
    
    const makeApiCall = async () => {
      if (requestData && !isProcessingRef.current) {
        isProcessingRef.current = true;
        
        const url = `${baseUrl}compute/lca/edit/${computeId}`;
        
        try {
          setIsLoading(true);
          
          const response = await fetch(url, {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              token: token || "",
            },
            body: JSON.stringify(requestData)
          });
          
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
          }
          
          const result = await response.json();
          
          // Handle the response based on mode
          if (requestData.is_draft === 1) {
            // Draft mode
            close();
            notifications.show({
              title: 'Success',
              message: 'File Saved in Draft Successfully',
              color: 'green',
            });
            navigate('/draft');
          } else {
            // Next mode - call onComplete AFTER successful API response (like Transport component)
            hasCompletedRef.current = true;
            onComplete();
            
            notifications.show({
              title: 'Success',
              message: 'Component Data Saved',
              color: 'green',
            });
          }
          
          // Reset requestData to prevent duplicate processing
          setRequestData(null);
          
        } catch (error) {
          console.error('API Error:', error);
          notifications.show({
            title: 'Error',
            message: 'An error occurred while saving',
            color: 'red',
          });
        } finally {
          setIsLoading(false);
          isProcessingRef.current = false;
        }
      }
    };

    makeApiCall();
  }, [requestData, computeId, token, onComplete, close, navigate]);

  const handleClickEdit = (id: number) => {
    navigate(`${editLink}/${id}`);
  };

  const handleOpenDeleteModal = (id: any) => {
    setSelectedId(id);
    openConfirmDelete();
  };

  const handleView = (id: any) => {
    setSelectedComputeId(id);
    openViewModal();
  };
  // useEffect(() => {
  //   if (datalistview) {
  //     console.log("one", datalistview?.data?.rows[0]?.ProductComponents[0])
  //     console.log("two", datalistview?.data?.rows[0]?.ProductComponents[1])

  //   }

  // }, [datalistview])

  const handleCloseSuccessModal = () => {
    setRefresh(prev => prev + 1);
    closeSuccessModal();
  };
  const handleAddComponentSuccess = () => {
    // Refresh the data after adding a component
    setRefresh(prev => prev + 1);
    closeAddComponentModal();
  };

  const validateFileName = () => {
    let newErrors: ErrorsType = {};

    if (!fileName.trim()) {
      newErrors.file_name = "File name is required";
    } else if (fileName.length < 3) {
      newErrors.file_name = "File name must be at least 3 characters long";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0; // Return true if there are no errors
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFileName(e.target.value);

    // Safely check and clear the file_name error
    if (errors?.file_name) {
      setErrors((prev: any) => ({ ...prev, file_name: undefined }));
    }
  };

  const handlesave = (e: any) => {
    e.preventDefault();
    const isValid = validateFileName();

    if (!isValid) return; // Only proceed if validation passes

    // Set request data to trigger the API call via useEffect
    const data = {
      is_draft: 1,
      step: 'Components',
      compute_id: computeId,
      file_name: fileName
    };
    
    setRequestData(data);
  };
  const handleComplete = (e: any) => {
    e.preventDefault();
    
    // Set draft state BEFORE API call (like Transport component)
    setDraftLca(0);
    
    // Set request data to trigger the API call via useEffect
    const data = {
      is_draft: 0,
      step: 'Components',
      compute_id: computeId,
      file_name: null
    };
    
    setRequestData(data);
  }

  const GSM = datalist ? datalist?.data?.rows[0]?.ComputeComponents[0]?.gsm_size === null ? false : true : '';

  const rows = datalist?.data?.rows[0]?.ComputeComponents?.map((el: any, index: number) => (
    <Table.Tr key={el.id}>
      <Table.Td>{index + 1}</Table.Td>
      <Table.Td>{el?.Vertical?.vertical_name}</Table.Td>
      <Table.Td>{el?.RawMaterial?.material_name}</Table.Td>
      <Table.Td>{el?.RawMaterialSubType?.sub_name}</Table.Td>
      {/* <Table.Td>{el?.water_consumption}</Table.Td> */}
      <Table.Td>{el?.weight || 'N/A'}</Table.Td>
      <Table.Td>{el?.percentage || 'N/A'}</Table.Td>
      <Table.Td>{el?.gsm_size || 'N/A'}</Table.Td>
      <Table.Td>{el?.sheet_size_height || 'N/A'}</Table.Td>
      <Table.Td>{el?.sheet_size_width || 'N/A'}</Table.Td>
      <Table.Td>{el?.no_of_product || 'N/A'}</Table.Td>
      {/* <Table.Td>{el?.no_of_layer || 'N/A'}</Table.Td>
      <Table.Td>{el?.glue_type_co2 || 'N/A'}</Table.Td>
      <Table.Td>{el?.coverage || 'N/A'}</Table.Td>
      <Table.Td>{el?.ink_type_co2 || 'N/A'}</Table.Td> */}

      <Table.Td style={{ display: 'flex', alignItems: 'center' }}>
        <ActionIcon color="#404040" variant="subtle" onClick={() => handleView(el?.id)}>
          <MdOutlineRemoveRedEye size={18} />
        </ActionIcon>
        {/* <ActionIcon color="#404040" variant="subtle" onClick={() => handleClickEdit(el.id)}>
          <FaRegEdit size={16} />
          </ActionIcon> */}
        {datalist?.data?.rows[0]?.ComputeComponents?.length > 1 && (
          <ActionIcon color="red" variant="subtle" onClick={() => handleOpenDeleteModal(el?.id)}>
            <IoTrashOutline size={16} />
          </ActionIcon>
        )}
      </Table.Td>
    </Table.Tr>
  ));

  const totalPages = Math.ceil((datalist?.data?.count || 0) / limit);

  return (
    <div style={{ padding: '10px' }}>
      <LoadingOverlay
        visible={listLoading}
        zIndex={1000}
        overlayProps={{
          radius: 'sm',
          blur: 2,
          style: {
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%'
          }
        }}
        loaderProps={{ color: '#404040', type: 'bars' }}
      />
      <Box>
        <Flex
          gap="md"
          justify="space-between"
          align="center"
          direction="row"
          wrap="wrap">
          <Title order={3}>{datalist?.data?.rows[0]?.product_name}</Title>
          <Button
            variant="gradient"
            gradient={{ from: '#404040', to: '#404040', deg: 90 }}
            radius={6}
            leftSection={<IoAddOutline size={20} />}
            onClick={openAddComponentModal}
            // loading={factoryLoading}
            // disabled={factoryLoading}
            size="md"
          >
            Add/Edit Component
          </Button>
        </Flex>
      </Box>
      <Space h={'md'} />
      {/* Table */}
      <Table.ScrollContainer minWidth={1500}>
        <Table striped className={classes.customtable}>
          <Table.Thead>
            <Table.Tr style={{ backgroundColor: '#4040401A' }}>
              <Table.Th>Component</Table.Th>
              <Table.Th>Vertical Name</Table.Th>
              <Table.Th>Raw Material</Table.Th>
              <Table.Th>Raw Material Sub Type</Table.Th>
              {/* <Table.Th>Water Consumption</Table.Th> */}
              <Table.Th>Weight</Table.Th>
              <Table.Th>Percentage</Table.Th>
              <Table.Th>GSM Size</Table.Th>
              <Table.Th>Sheet Size Height</Table.Th>
              <Table.Th>Sheet Size Width</Table.Th>
              <Table.Th>No. of Product</Table.Th>
              {/* <Table.Th>No. of Layers</Table.Th>
              <Table.Th>Glue Type CO₂/cm</Table.Th>
              <Table.Th>Coverage</Table.Th>
              <Table.Th>Ink Type CO₂/cm</Table.Th> */}
              <Table.Th>Action</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>{rows}</Table.Tbody>
        </Table>
      </Table.ScrollContainer>

      {/* Pagination */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '20px' }}>
        <Select
          data={[
            { value: '5', label: '05 per page' },
            { value: '10', label: '10 per page' },
            { value: '20', label: '20 per page' },
          ]}
          value={limit.toString()}
          onChange={(value) => setLimit(Number(value))}
          style={{ width: '150px' }}
        />
        <Pagination total={totalPages} value={page} onChange={setPage} color="#404040" />
      </div>
      <DeleterowData
        isConfirmOpen={confirmDeleteOpened}
        isSuccessOpen={successModalOpened}
        closeConfirm={closeConfirmDelete}
        closeSuccess={handleCloseSuccessModal}
        selectedId={selectedId}
        onDeleteSuccess={openSuccessModal}
      />

      <ViewProductRow
        isOpen={viewModalOpened}
        onClose={closeViewModal}
        componentId={selectedComputeId}
      />
      <Space h="lg" />
      <Space h="sm" />

      <Modal centered opened={opened} onClose={close} title="Save in Draft">
        <TextInput
          autoFocus
          label="File Name"
          value={fileName}
          onChange={handleChange}
          variant="filled"
          withAsterisk
          mb="lg"
          placeholder="Please enter file name"
          radius={6}
          error={errors?.file_name}
        />
        <Divider h={'md'} />
        <Flex justify="flex-end">
          <Button
            variant="gradient"
            gradient={{ from: '#404040', to: '#404040', deg: 90 }}
            radius={6}
            onClick={handlesave}
            loading={isLoading}
            disabled={isLoading}
            size="md"
          >
            Save
          </Button>
        </Flex>

      </Modal>
      <Flex
        justify="flex-end"
      >
        <Group >
          <Button
            variant="default"
            radius={6}
            // onClick={() => navigate('/manage-factory')}
            size="md"
            onClick={open}
          >
            Save to Draft
          </Button>
          <Button
            variant="gradient"
            gradient={{ from: '#404040', to: '#404040', deg: 90 }}
            radius={6}
            onClick={(e) => {
              handleComplete(e)
            }}
            loading={isLoading}
            disabled={isLoading}
            size="md"
          >
            Next
          </Button>
        </Group>
      </Flex>
      <Modal
        opened={addComponentModalOpened}
        onClose={closeAddComponentModal}
        title="Edit Component"
        size="xl"
        padding="md"
        centered
        styles={{
          content: {
            height: 'auto',
          },
        }}
      > <ProductDetailsComponent
          id_c={computeId}
          onClose={handleAddComponentSuccess}
          gsm={GSM}
        />
      </Modal>
    </div>
  );
}

export default memo(ProductTable);
